// Mirror of backend/internal/protocol (Go). Keep these in sync with the server.

export type GameStatus = 'lobby' | 'active' | 'finished';
export type QuestionState = 'idle' | 'open' | 'closed';
export type QuestionMode = 'classic' | 'shout';
export type BuzzResult = 'pending' | 'correct' | 'incorrect';

// Client -> Server actions
export const Action = {
  PlayerJoin: 'player.join',
  PlayerIKnow: 'player.iKnow',
  AdminStart: 'admin.startGame',
  AdminOpenQ: 'admin.openQuestion',
  AdminResetQ: 'admin.resetQuestion',
  AdminCallOn: 'admin.callOn',
  AdminMark: 'admin.mark',
  AdminUnmark: 'admin.unmark',
  AdminSetMode: 'admin.setQuestionMode',
  AdminAward: 'admin.awardShout',
  AdminNextQ: 'admin.nextQuestion',
  AdminPrevQ: 'admin.prevQuestion',
  AdminNextTop: 'admin.nextTopic',
  AdminPrevTop: 'admin.prevTopic',
  AdminEndGame: 'admin.endGame',
  Sync: 'sync',
} as const;

// Server -> Client events
export const Event = {
  GameState: 'game.state',
  Welcome: 'player.welcome',
  PlayerJoined: 'player.joined',
  QuestionOpen: 'question.opened',
  QueueUpdate: 'queue.updated',
  QuestionShut: 'question.closed',
  CalledOn: 'player.calledOn',
  AnswerResult: 'answer.result',
  ScoreUpdate: 'score.updated',
  GameEnded: 'game.ended',
  Error: 'error',
} as const;

export interface ClientMessage {
  action: string;
  data?: unknown;
}

export interface ServerMessage {
  type: string;
  data?: unknown;
}

export interface PublicQuestion {
  id: string;
  text: string;
  points: number;
  order: number;
}

export interface QueueEntry {
  playerId: string;
  nickname: string;
  seq: number;
  result: BuzzResult;
  /** ms after the tier reference (first correct buzz, else first buzz); negative = earlier */
  deltaMs: number;
  /** what marking this buzz correct would award right now (0 = too late) */
  pointsOnCorrect: number;
  /** what a correct mark actually granted */
  pointsAwarded: number;
}

export interface ScoreEntry {
  playerId: string;
  nickname: string;
  score: number;
}

export interface GameStateData {
  gameId: string;
  code: string;
  status: GameStatus;
  questionState: QuestionState;
  questionMode: QuestionMode;
  topicTitle?: string;
  question?: PublicQuestion;
  endsAt?: string;
  queue: QueueEntry[];
  scores: ScoreEntry[];
  answerNote?: string;
}

export interface QuestionOpenData {
  question: PublicQuestion;
  topicTitle: string;
  endsAt: string;
  answerNote?: string;
}

export interface WelcomeData {
  playerId: string;
  nickname: string;
  gameId: string;
}

export interface CalledOnData {
  playerId: string;
  nickname: string;
}

export interface AnswerResultData {
  playerId: string;
  nickname: string;
  correct: boolean;
  points: number;
}

export interface ErrorData {
  message: string;
}

/**
 * Points each of `n` shouting-match winners gets for a question worth `price`:
 * the pot split equally and rounded up, but never below price/10 (also rounded up).
 * Mirrors game.ShoutPoints in the Go backend — keep the two in sync.
 */
export function shoutPoints(price: number, n: number): number {
  if (n <= 0 || price <= 0) return 0;
  const ceilDiv = (a: number, b: number) => Math.ceil(a / b);
  return Math.max(ceilDiv(price, n), ceilDiv(price, 10));
}
